import React, { useEffect, useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { JsonIndi } from '@/familyTree/Topola';
import { ReportMonitor } from '@/monitoring/MonitoredReportsModal/Components';
import { useReportMonitors } from '@/monitoring/useReportMonitors';
import { Owner } from '@/reports/PersonBasedReport';
import { ReportFactory, useReport } from '@/reports/index';
import { reportTypes } from '@/reports/types';
import { Stack, Text, Grid, Card, styled } from '@/theme/index';
import FamilyInsights from './FamilyInsights';
import { FamilyRelativesReports } from './FamilyRelativesReports/FamilyRelativesReports';
import { FamilyTreeBadge } from './FamilyTreeBadge';
import { FamilyTreeFeaturesPreview } from '../FamilyTreeFeaturesPreview/FamilyTreeFeaturesPreview';
import TreeBuilderBanner from '../FamilyTreeIABuilder/Banner';
import TreeBuilderGetStarted from '../FamilyTreeIABuilder/GetStarted';
import { FamilyTreeMap } from '../FamilyTreeMap/FamilyTreeMap';
import { useAncestryTree } from '../useFamilyTree/useFamilyTree';

const StyledStack = styled(Stack)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    flexDirection: 'row',
  },
}));

const REPORT_TYPE = 'detailed_person_report';
const MAX_REPORTS = 10;
const ANCESTRY_TREE_INITIAL_MEMBER_COUNT = 6;

const getClaimedReports = (monitorReports: ReportMonitor[]) => {
  if (!monitorReports.length) return [];

  const claimedReports = monitorReports
    .filter(
      (report) =>
        report.report_type === REPORT_TYPE && Boolean(report.is_claimed)
    )
    .slice(0, 10);

  return claimedReports;
};

export const FamilyAndAncestryDashboard = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const openParam = urlParams.get('open') ?? undefined;
  const { config } = useAppConstantsContext();
  const { routingUtils } = useAppConfigContext();
  const { navigate } = routingUtils;
  const [showTreeBuilderGettingStarted, setShowTreeBuilderGettingStarted] =
    useState<boolean>(openParam === 'tree-builder' || false);
  const [bvid, setBvid] = useState<string>('');
  const [permalink, setPermalink] = useState<string>('');
  const permalinkParam = urlParams.get('from') || '';
  const [report, setReport] = useState<Owner | undefined>(undefined);
  const [reportIsBeingFetched, setReportIsBeingFetched] = useState<boolean>(
    showTreeBuilderGettingStarted
  );
  const {
    currentMonitors,
    reportIsMonitored,
    updateMonitorMutation,
    toggleReportIsMonitored,
    reportMonitorIsLoading,
  } = useReportMonitors(permalinkParam);

  const { data: coreResultData, isError: reportIsError } = useReport(
    permalink,
    !permalink
  );

  const { data: ancestryTreeData } = useAncestryTree({
    permalink,
  });

  const members: JsonIndi[] =
    ancestryTreeData?.indis.filter(
      (indi) => indi && !indi.placeholder && indi.bvid
    ) || [];
  // Sort members by most recent added
  members.sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  const getOwner = (coreResultData: reportTypes.Report) => {
    const report = ReportFactory.create(coreResultData, 'person');

    const owner = report.getOwner({
      potentialOwnerIndex: 0,
      showHighConfidenceDataOnly: true,
      higherConfidenceThreshold: config.higherConfidenceThreshold,
    });

    return owner;
  };

  useEffect(() => {
    if (!!currentMonitors && permalink === '') {
      const claimedReports = getClaimedReports(currentMonitors);
      if (claimedReports.length > 0) {
        setPermalink(claimedReports[0].permalink);
      } else {
        setTimeout(() => {
          if (!permalink && !reportMonitorIsLoading) {
            setReportIsBeingFetched(false);
          }
        }, 1500);
      }
    }
  }, [
    currentMonitors,
    permalink,
    setPermalink,
    setReportIsBeingFetched,
    reportMonitorIsLoading,
  ]);

  useEffect(() => {
    if (coreResultData) {
      const owner = getOwner(coreResultData);
      const { identity } = owner;

      setReport(owner);
      setBvid(identity!.ids[0]);

      setReportIsBeingFetched(false);
    }
  }, [coreResultData, setBvid, setReport, setReportIsBeingFetched]);

  useEffect(() => {
    if (reportIsError) {
      setReportIsBeingFetched(false);
    }
  }, [reportIsError, setReportIsBeingFetched]);

  const claimCurrentReport = () => {
    const permalinkParam = urlParams.get('from') || '';

    const queryParams = {
      'report_monitor[permalink]': permalinkParam as string,
      'report_monitor[is_claimed]': 'true',
      'report_monitor[report_type]': 'detailed_person_report',
    };

    if (reportIsMonitored) {
      updateMonitorMutation.mutate({
        permalinkToEdit: permalinkParam,
        bodyParams: {
          report_monitor: {
            is_claimed: true,
          },
        },
      });
      // If the user has not reached the limit of reports, we add the report to the monitor with is_claimed = true
    } else if (currentMonitors?.length < MAX_REPORTS) {
      toggleReportIsMonitored({
        queryParams,
        successCallback: () => {
          // navigate('/dashboard/family-ancestry?open=tree-builder');
        },
        errorCallback: () => navigate('/dashboard'),
      });
    }
  };

  let shouldSeeTreeBuilder = false;
  if (!ancestryTreeData) {
    shouldSeeTreeBuilder = true;
  }
  if (ancestryTreeData) {
    // We don't render the tree builder if there are more than 6 relatives
    // this means user has already manipulated the tree by adding or removing members
    if (ancestryTreeData.indis.length <= ANCESTRY_TREE_INITIAL_MEMBER_COUNT) {
      shouldSeeTreeBuilder = true;
    } else {
      // We don't render the tree if there is one relative added for the main user
      const treeRelativesCount =
        ancestryTreeData.indis.filter((indi) => indi && !indi.placeholder)
          .length || 0;
      shouldSeeTreeBuilder = treeRelativesCount < 1;
    }
  }

  if (showTreeBuilderGettingStarted) {
    return (
      <TreeBuilderGetStarted
        bvid={bvid}
        permalink={permalink}
        report={report}
        reportClaimed={!!report}
        reportIsLoading={reportIsBeingFetched}
        backToFamilyAncestryDashboard={() =>
          setShowTreeBuilderGettingStarted(false)
        }
        shouldSeeTreeBuilder={shouldSeeTreeBuilder}
        claimCurrentReport={claimCurrentReport}
      />
    );
  }

  document.title = 'Family & Ancestry Dashboard';

  return (
    <Stack>
      <StyledStack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Text variant="h1" marginBottom={3}>
          Family & Ancestry
        </Text>
        {report && (
          <Stack
            data-cy="family-tree-badge-component"
            sx={{
              width: ['100%', 'unset'],
            }}
          >
            <FamilyTreeBadge report={report} permalink={permalink} />
          </Stack>
        )}
      </StyledStack>
      <Stack>
        <Text variant="h3" marginBottom={3}>
          Main Features
        </Text>
        <StyledStack>
          <Grid
            sx={{
              marginRight: [0, 2],
            }}
            sm={12}
            md={6}
          >
            <Card
              sx={{
                padding: [2],
                width: '100%',
              }}
              variant="outlined"
            >
              <Stack data-cy="family-tree-features-preview-component">
                <FamilyTreeFeaturesPreview
                  ancestryTreeData={ancestryTreeData}
                  permalink={permalink}
                  fromDashboard
                />
              </Stack>
            </Card>
          </Grid>
          <Grid sm={12} md={6}>
            <Card
              sx={{
                padding: [2],
                width: '100%',
              }}
              variant="outlined"
            >
              <Stack data-cy="family-tree-features-preview-component">
                <FamilyTreeMap
                  ancestryTreeData={ancestryTreeData}
                  permalink={permalink}
                  shouldSeeTreeBuilder={shouldSeeTreeBuilder}
                />
              </Stack>
            </Card>
            {shouldSeeTreeBuilder && (
              <TreeBuilderBanner
                setShowTreeBuilderGettingStarted={
                  setShowTreeBuilderGettingStarted
                }
                reportClaimed={!!report}
              />
            )}
          </Grid>
        </StyledStack>
      </Stack>
      <FamilyInsights
        permalink={permalink}
        ancestryTreeData={ancestryTreeData}
      />
      <FamilyRelativesReports members={members} />
    </Stack>
  );
};
